import React, { useRef } from 'react';
import { FieldsetModifier } from '../../../_lib/Form2/elements/Fieldset/Fieldset';
import {
  Checkbox,
  CheckBoxParam,
  DateRange,
  Fieldset,
  Input,
  NumberParam,
  RequiredHelpPhrase,
  Row,
  Switch,
  TimeRange,
  useCheckboxGroup,
} from '../../../_lib/Form2';
import { Step } from '../../../components/molecules/Form/components/MultiStepForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { DateRangeParam, TimeRangeParam } from '../../../_lib/Form2/types/DateRangeParam';

type TicketModalMultiStepFormTimeFrameStepProps = {
  stepId: string;
  dateRange: DateRangeParam<'daterange'>;
  weekDays: (
    | CheckBoxParam<'onMondays'>
    | CheckBoxParam<'onTuesdays'>
    | CheckBoxParam<'onWednesdays'>
    | CheckBoxParam<'onThursdays'>
    | CheckBoxParam<'onFridays'>
    | CheckBoxParam<'onSaturdays'>
    | CheckBoxParam<'onSundays'>
  )[];
  timeRange: TimeRangeParam<'timerange'>;
  parallelTickets: NumberParam<'parallelTickets'>;
  durationMinutes: NumberParam<'durationMinutes'>;
};

export function TicketModalMultiStepFormTimeFrameStep({
  stepId,
  dateRange,
  weekDays,
  timeRange,
  parallelTickets,
  durationMinutes,
}: TicketModalMultiStepFormTimeFrameStepProps) {
  const { t } = useSonTranslation('TicketBooking');

  const { setElementRef, onCheckboxChange } = useCheckboxGroup(t('edit.form.weekDays.errorMessage'));
  const durationMinutesRef = useRef<HTMLInputElement>(null);
  const timeAsDurationRef = useRef<number>(null);
  const timeRef = useRef<number[]>([-1, -1]);

  function setDurationMinutesValue() {
    if (durationMinutesRef.current && timeAsDurationRef.current) {
      durationMinutesRef.current.value = durationMinutesRef.current?.readOnly
        ? timeAsDurationRef.current?.toString()
        : '';
    }
  }

  return (
    <Step id={stepId} buttonLabel={t('edit.form.steps.timeframe')}>
      <RequiredHelpPhrase />
      <Fieldset legend={t('edit.form.legendTimeFrameDependent')}>
        <DateRange
          dateRangeParam={dateRange}
          labelStartDate={t(`edit.form.labelStartDate`)}
          labelEndDate={t(`edit.form.labelEndDate`)}
        />
        <Fieldset modifier={FieldsetModifier.LIKE_LABEL} required legend={t('edit.form.weekDaysLabel')}>
          <Row>
            {weekDays.map(param => {
              return (
                <Checkbox
                  key={param.name}
                  param={param}
                  ref={setElementRef}
                  onChange={onCheckboxChange}
                  label={
                    // the combination of aria-label and aria-hidden inside is the only way to make sure,
                    // this is announced correctly (full day, not abbreviated) in screen readers.
                    <span
                      aria-label={t(`edit.form.weekDays.${param.name}.title`)}
                      title={t(`edit.form.weekDays.${param.name}.title`)}
                    >
                      <span aria-hidden="true">{t(`edit.form.weekDays.${param.name}.abbr`)}</span>
                    </span>
                  }
                />
              );
            })}
          </Row>
        </Fieldset>
        <TimeRange
          timeRangeParam={timeRange}
          labelStartTime={t('edit.form.labelStartTime')}
          labelEndTime={t('edit.form.labelEndTime')}
          onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
            if (event.target?.value) {
              if (event.target?.name === 'startTime') {
                timeRef.current[0] = event.target?.valueAsNumber;
              } else if (event.target?.name === 'endTime') {
                timeRef.current[1] = event.target?.valueAsNumber;
              }
            }

            // initialized timeRef.current array with -1 as a null/undefined check as it was before would also
            // validate to false if the value is number 0 as it can be for 0:00 in the morning.
            if (timeRef.current[0] > -1 && timeRef.current[1] > -1) {
              timeAsDurationRef.current = Math.floor((timeRef.current[1] - timeRef.current[0]) / (1000 * 60));

              setDurationMinutesValue();
            }
          }}
        />
      </Fieldset>
      <Fieldset legend={t('edit.form.legendRuleDependent')}>
        <Row>
          <Switch
            label={t('edit.form.labelSwitchTimeAsDuration')}
            helpText={t(`edit.form.labelSwitchTimeAsDurationHelpText`)}
            onClick={() => {
              if (durationMinutesRef.current) {
                durationMinutesRef.current.readOnly = !durationMinutesRef.current?.readOnly;
              }

              setDurationMinutesValue();
            }}
          />
        </Row>
        <Row>
          <Input
            ref={durationMinutesRef}
            param={durationMinutes}
            label={t('edit.form.labelDurationMinutes')}
            inputMode="numeric"
            helpText={
              durationMinutes.constraints?.max
                ? t('edit.form.helpTextDurationMinutes', { max: durationMinutes.constraints.max })
                : undefined
            }
          />
        </Row>
        <Row>
          <Input
            param={parallelTickets}
            label={t('edit.form.labelParallelTickets')}
            helpText={t('edit.form.helpTextParallelTickets')}
            inputMode="numeric"
          />
        </Row>
      </Fieldset>
    </Step>
  );
}
